import React, { useState, useEffect, useMemo } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { FaSearch, FaPlus, FaEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import DepartmentTopbar from "../SuperAdmin/components/DepartmentTopbar";
import DepartmentSidebar from "../SuperAdmin/components/DepertmentSidebar";
import RoomRentModal from "./RoomRentModal";

function RoomRent() {
  const [roomRentData, setRoomRentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("all");

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem("token");

  const fetchRoomRentData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/list-room-rent.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success && Array.isArray(response.data.data)) {
        setRoomRentData(response.data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch room rent data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoomRentData();
  }, []);

  const handleAdd = () => {
    setEditingData(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditingData(row);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const response = await axios.post(
          `${API_URL}/delete-room-rent.php`,
          { id },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response.data.success) {
          toast.success("Data deleted successfully!");
          fetchRoomRentData();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("An error occurred during deletion.");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingData(null);
  };

  const handleSave = () => {
    fetchRoomRentData();
    handleModalClose();
  };

  const filteredData = useMemo(() => {
    return roomRentData.filter((item) => {
      const matchesSearch = item.challan_no
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesPaymentType =
        paymentTypeFilter === "all" || item.payment_type === paymentTypeFilter;

      const itemDate = new Date(item.collection_date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;

      return matchesSearch && matchesPaymentType;
    });
  }, [roomRentData, searchTerm, startDate, endDate, paymentTypeFilter]);

  const { totalAmount, totalInterest } = useMemo(() => {
    return filteredData.reduce(
      (acc, item) => {
        acc.totalAmount += parseFloat(item.total_amount || 0);
        acc.totalInterest += parseFloat(item.inrest_amount || 0);
        return acc;
      },
      { totalAmount: 0, totalInterest: 0 }
    );
  }, [filteredData]);

  const handleExport = () => {
    const dataToExport = filteredData.map((d) => ({
      "Challan No": d.challan_no,
      "Total Amount": d.total_amount,
      "Interest Amount": d.inrest_amount,
      "Payment Type": d.payment_type,
      "Collection Date": d.collection_date,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "RoomRentData");
    XLSX.writeFile(workbook, "Room_Rent_Report.xlsx");
  };

  const columns = [
    { name: "Challan No", selector: (row) => row.challan_no, sortable: true },
    {
      name: "Total Amount (₹)",
      selector: (row) => parseFloat(row.total_amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Interest (₹)",
      selector: (row) => parseFloat(row.inrest_amount).toLocaleString("en-IN"),
      sortable: true,
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type,
      sortable: true,
      cell: (row) => (
        <span className="text-capitalize">{row.payment_type}</span>
      ),
    },
    {
      name: "Collection Date",
      selector: (row) => row.collection_date,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <FaEdit />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row.id)}
          >
            <FaTrash />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <div className="flex-grow-1 p-3" style={{ background: "#f0f2f5" }}>
          <Card className="shadow-sm">
            <Card.Header
              as="h5"
              className="d-flex justify-content-between align-items-center"
            >
              Room Rent Management
              <Button variant="primary" size="sm" onClick={handleAdd}>
                <FaPlus /> Add New
              </Button>
            </Card.Header>
            <Card.Body>
              <Form className="p-3 border-bottom">
                <Row className="g-3 align-items-end">
                  <Col md={3}>
                    <Form.Label>Search by Challan No</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="search"
                        placeholder="Enter challan no..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={2}>
                    <Form.Label>Payment Type</Form.Label>
                    <Form.Select
                      value={paymentTypeFilter}
                      onChange={(e) => setPaymentTypeFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="cash">Cash</option>
                      <option value="online">Online</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={3} className="text-md-end">
                    <Button onClick={handleExport} variant="success">
                      <FaFileExcel className="me-2" />
                      Export
                    </Button>
                  </Col>
                </Row>
              </Form>
              <Row className="p-3 text-center">
                <Col md={4}>
                  <Card bg="light">
                    <Card.Body>
                      <Card.Title>Total Collection</Card.Title>
                      <Card.Text className="fs-4 fw-bold">
                        ₹{totalAmount.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card bg="light">
                    <Card.Body>
                      <Card.Title>Total Interest</Card.Title>
                      <Card.Text className="fs-4 fw-bold">
                        ₹{totalInterest.toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card bg="primary" text="white">
                    <Card.Body>
                      <Card.Title>Grand Total</Card.Title>
                      <Card.Text className="fs-4 fw-bold">
                        ₹{(totalAmount + totalInterest).toLocaleString("en-IN")}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                responsive
                progressPending={loading}
              />
            </Card.Body>
          </Card>
        </div>
      </div>
      {showModal && (
        <RoomRentModal
          show={showModal}
          handleClose={handleModalClose}
          onSave={handleSave}
          data={editingData}
        />
      )}
    </>
  );
}

export default RoomRent;
